import React, { useState, useEffect } from 'react';
import { versionAPI } from '../api';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { History, RotateCcw, Eye, Loader2, Clock, User, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const VersionHistory = ({ entityType, entityId, isOpen, onClose, onRevert }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [reverting, setReverting] = useState(false);

  useEffect(() => {
    if (isOpen && entityType && entityId) {
      fetchVersions();
    }
  }, [isOpen, entityType, entityId]);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const res = await versionAPI.getVersions(entityType, entityId);
      setVersions(res.data || []);
    } catch (err) {
      console.error('Failed to fetch versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRevert = async (versionId, versionNum) => {
    setReverting(true);
    try {
      await versionAPI.revert(versionId);
      toast.success(`Reverted to version ${versionNum}`);
      fetchVersions();
      if (onRevert) onRevert();
    } catch (err) {
      toast.error('Failed to revert');
    } finally {
      setReverting(false);
    }
  };

  const handleDelete = async (versionId) => {
    try {
      await versionAPI.delete(versionId);
      toast.success('Version deleted');
      fetchVersions();
    } catch (err) {
      toast.error('Failed to delete version');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-[#00A3A3]" />
            Version History
          </DialogTitle>
          <DialogDescription>
            View and revert to previous versions of this document
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-[#00A3A3]" />
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-8">
            <History className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
            <p className="text-sm text-[#5A6570]">No version history yet</p>
            <p className="text-xs text-[#A8B1B9] mt-1">Versions are saved automatically when you edit documents</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="version-list">
            {versions.map((v, idx) => (
              <div
                key={v.id}
                className={`border rounded-lg p-3 transition-colors ${
                  idx === 0 ? 'border-[#00A3A3] bg-[#00A3A3]/5' : 'border-[#E2E6EA] hover:border-[#00A3A3]/50'
                }`}
                data-testid={`version-item-${v.version_number}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-[#1A1D21]">
                        v{v.version_number}
                      </span>
                      {idx === 0 && (
                        <Badge className="bg-[#00A3A3] text-white text-[10px] h-5">Current</Badge>
                      )}
                    </div>
                    {v.change_summary && (
                      <p className="text-xs text-[#5A6570] mt-1">{v.change_summary}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-[#A8B1B9]">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {v.created_by_name || 'System'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {v.created_at ? new Date(v.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                        }) : ''}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {selectedVersion === v.id ? (
                      <div className="bg-[#F0F2F4] rounded p-2 max-w-[200px] max-h-[100px] overflow-auto text-[10px] text-[#5A6570]">
                        <div dangerouslySetInnerHTML={{ __html: (v.content || '').slice(0, 300) + '...' }} />
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setSelectedVersion(selectedVersion === v.id ? null : v.id)}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {idx !== 0 && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-[#00A3A3]"
                          onClick={() => handleRevert(v.id, v.version_number)}
                          disabled={reverting}
                          data-testid={`revert-btn-${v.version_number}`}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-400 hover:text-red-600"
                          onClick={() => handleDelete(v.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VersionHistory;
